import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageList } from "../../components/pageList";
import { Badge, Button, Group, Modal, NumberInput, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/zh-tw";
import "moment/locale/zh-cn";
import { uiLocale } from "../../helpers/uiLocale";
import { useServerApi } from "../../hooks/userServerApi";
import { notifyListRefresh } from "../../context/tabContext";

const apiEntity = "fiscalPeriod";

const FiscalPeriodActions = ({ data, onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [api] = useServerApi();
  const [loading, setLoading] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      if (data.status === "OPEN") {
        await api.Accounting.closeFiscalPeriod(data._id);
        showNotification({
          title: t("accounting.fiscalPeriod.closedTitle"),
          message: data.name,
          color: "green",
        });
      } else {
        await api.Accounting.reopenFiscalPeriod(data._id);
        showNotification({
          title: t("accounting.fiscalPeriod.reopenedTitle"),
          message: data.name,
          color: "green",
        });
      }
      onRefresh();
    } catch (error) {
      showNotification({
        color: "red",
        title: t("accounting.fiscalPeriod.actionFailed"),
        message: String(error?.message || error),
      });
    } finally {
      setLoading(false);
    }
  };

  const isOpen = data.status === "OPEN";

  return (
    <Button
      size="xs"
      variant={isOpen ? "filled" : "outline"}
      loading={loading}
      onClick={handleClick}
    >
      {isOpen
        ? t("accounting.fiscalPeriod.close")
        : t("accounting.fiscalPeriod.reopen")}
    </Button>
  );
};

const buildTableSchema = (t, i18n, onRefresh) => ({
  showActionColumn: false,
  disableRowDoubleClick: true,
  columns: [
    {
      field: "name",
      headerName: "Name",
      sortable: true,
    },
    {
      field: "startDate",
      headerName: "accounting.fiscalPeriod.start",
      sortable: true,
      cellRender: (col, data) =>
        data.startDate
          ? moment(data.startDate)
              .locale(uiLocale(i18n.language))
              .format("LL")
          : "",
    },
    {
      field: "endDate",
      headerName: "accounting.fiscalPeriod.end",
      sortable: true,
      cellRender: (col, data) =>
        data.endDate
          ? moment(data.endDate)
              .locale(uiLocale(i18n.language))
              .format("LL")
          : "",
    },
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      cellRender: (col, data) => (
        <Badge color={data.status === "OPEN" ? "green" : "gray"}>
          {data.status === "OPEN"
            ? t("accounting.fiscalPeriod.statusOpen")
            : t("accounting.fiscalPeriod.statusClosed")}
        </Badge>
      ),
    },
    {
      field: "periodAction",
      headerName: "Action",
      sortable: false,
      cellRender: (col, data) => (
        <FiscalPeriodActions data={data} onRefresh={onRefresh} />
      ),
    },
  ],
  searchableFields: ["name"],
});

const FiscalPeriods = () => {
  const { t, i18n } = useTranslation();
  const [api] = useServerApi();
  const location = useLocation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const refreshList = () => notifyListRefresh(location.pathname);

  const tableSchema = useMemo(
    () => buildTableSchema(t, i18n, refreshList),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, location.pathname]
  );

  const customActions = useMemo(
    () => [
      {
        label: t("accounting.fiscalPeriod.createYear"),
        requiresWrite: true,
        onClick: () => setCreateOpen(true),
      },
    ],
    [t]
  );

  const handleCreateYear = async () => {
    if (!year) return;
    setCreating(true);
    try {
      await api.Accounting.createYearWithPeriods(year, `FY ${year}`);
      showNotification({
        title: t("accounting.fiscalPeriod.createSuccessTitle"),
        message: t("accounting.fiscalPeriod.createSuccessMessage", { year }),
        color: "green",
      });
      setCreateOpen(false);
      refreshList();
    } catch (error) {
      showNotification({
        color: "red",
        title: t("accounting.fiscalPeriod.createFailed"),
        message: String(error?.message || error),
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <PageList
        title={t("accounting.modules.fiscalPeriods.label")}
        apiEntity={apiEntity}
        tableSchema={tableSchema}
        formSchema={{ apiEntity, layout: { fields: [] } }}
        showAddButton={false}
        customActions={customActions}
        initSort={{ by: "name", order: "asc" }}
        defaultPageSize={50}
      />

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("accounting.fiscalPeriod.createYear")}
        size="sm"
      >
        <Text size="sm" color="dimmed" mb="md">
          {t("accounting.fiscalPeriod.createYearDesc")}
        </Text>
        <NumberInput
          label={t("accounting.fiscalPeriod.year")}
          value={year}
          onChange={setYear}
          min={2000}
          max={2100}
        />
        <Group position="right" mt="xl">
          <Button variant="default" onClick={() => setCreateOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button loading={creating} onClick={handleCreateYear}>
            {t("Confirm")}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default FiscalPeriods;
